/**
 * Convert a parcel record (from `listTenantParcels`) into renderable polygon
 * positions in Leaflet's [lat, lng] order. Supports both Polygon and
 * MultiPolygon GeoJSON shapes.
 */

export interface ParcelRecord {
  LISKEY?: string | null;
  parcel_ref?: string | null;
  ParcelNo?: string | null;
  PortionNo?: string | null;
  geometry?: {
    type?: string;
    coordinates?: unknown;
  } | null;
  // Enrichment fields carried by the /parcels/by-tenant payload — used for
  // labels (area/centroid LOD), the detail drawer (address), and client-side
  // per-boundary filtering. Optional so lighter callers stay valid.
  shape_area?: number | null; // hectares
  centroid_lat?: number | null;
  centroid_lon?: number | null;
  StreetNo?: string | null;
  StreetName?: string | null;
  TownshipFarmName?: string | null;
  Region?: string | null;
  Ward?: string | number | null;
  boundary_name?: string | null;
}

/** Viewport bbox for `filterParcelsToBbox` (matches BoundsObserver's shape). */
export interface ParcelBbox {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
}

export interface ParcelPolygon {
  key: string;
  positions: [number, number][];
  ref: string | null;
  label: string | null;
  linked: boolean;
}

/**
 * How much of a member a parcel is. Served by
 * `GET /parcels/by-tenant/{id}/member-status`; parcels absent from that
 * response are `unlinked`.
 */
export type ParcelStatus =
  | 'unlinked'
  | 'non_member'
  | 'member_unpaid'
  | 'member_paid';

/**
 * The parcel colour ramp — grey for "not a member", blue for members.
 *
 * Parcels live in the `parcelTiles` pane under every other layer, so they're a
 * background field, not a foreground signal: active/resolved fires (red,
 * green), boundaries (purple/orange dashed) and the wind ramp (full spectrum)
 * all paint on top. The ramp therefore spends exactly one hue — blue — and it
 * spends it only on members, deepening with payment. Everything that is not a
 * member is grey, which collides with nothing on any basemap.
 *
 * Two greys, separated by weight rather than by hue: `unlinked` is deliberately
 * near-invisible (most parcels in a tenant have no owner row at all), while
 * `non_member` is a solid mid-grey, because "we know this property and they
 * are not a member" is a real answer and should look like one.
 *
 * Within the blue half the stroke climbs alongside the fill on purpose. Fill
 * lightness alone is unreliable over satellite imagery (0.38 vs 0.52 of the
 * same blue is genuinely hard to separate on dark or bright ground); a
 * darkening, thickening outline stays legible on any basemap.
 */
export const PARCEL_STYLE_UNLINKED = {
  color: '#9e9e9e',
  fillColor: '#d6d6d6',
  // Deliberately near-invisible: most parcels in a tenant are unlinked, so
  // anything heavier blankets the basemap you need to navigate during a fire.
  fillOpacity: 0.1,
  weight: 0.75,
} as const;

export const PARCEL_STYLE_NON_MEMBER = {
  color: '#212121',
  fillColor: '#424242',
  fillOpacity: 0.22,
  weight: 1,
} as const;

export const PARCEL_STYLE_MEMBER_UNPAID = {
  color: '#1976d2',
  fillColor: '#64b5f6',
  fillOpacity: 0.38,
  weight: 1.5,
} as const;

export const PARCEL_STYLE_MEMBER_PAID = {
  color: '#0d47a1',
  fillColor: '#1976d2',
  fillOpacity: 0.52,
  weight: 2,
} as const;

// A single highlighted parcel (e.g. the parcel a member is linked to on their
// detail map) — purple outline over the top of the ramp's fill.
export const PARCEL_STYLE_HIGHLIGHT = {
  color: '#7b1fa2',
  fillColor: '#1976d2',
  fillOpacity: 0.52,
  weight: 2.5,
} as const;

export interface ParcelStyle {
  color: string;
  fillColor: string;
  fillOpacity: number;
  weight: number;
}

export const PARCEL_STYLE_BY_STATUS: Record<ParcelStatus, ParcelStyle> = {
  unlinked: PARCEL_STYLE_UNLINKED,
  non_member: PARCEL_STYLE_NON_MEMBER,
  member_unpaid: PARCEL_STYLE_MEMBER_UNPAID,
  member_paid: PARCEL_STYLE_MEMBER_PAID,
};

/**
 * The ramp as legend rows, defined once so every parcel map explains the
 * colours the same way. Feed straight to `<MapLegend items={...} />`.
 */
export const PARCEL_LEGEND_ITEMS = [
  { section: 'Parcels', label: 'Not linked', ...PARCEL_STYLE_UNLINKED },
  { section: 'Parcels', label: 'Non-member', ...PARCEL_STYLE_NON_MEMBER },
  { section: 'Parcels', label: 'Member — unpaid', ...PARCEL_STYLE_MEMBER_UNPAID },
  { section: 'Parcels', label: 'Member — paid', ...PARCEL_STYLE_MEMBER_PAID },
] as const;

// Single source of truth for parcel-label level-of-detail and font. A
// parcel's number is shown only once its on-screen footprint clears this
// many pixels — keeps dense areas legible across every map.
export const LABEL_MIN_PX = 34;
export const LABEL_FONT = '600 11px system-ui, sans-serif';

/** Web-Mercator metres-per-pixel at a given zoom + latitude. */
export const metersPerPixel = (zoom: number, lat: number): number =>
  (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;

/**
 * Should a parcel show its ParcelNo label at the current zoom? True once the
 * parcel's estimated on-screen side length (√area) clears `minPx`. Used for
 * the "zoom in to see numbers" hint and any DOM-label LOD. `shape_area` is in
 * hectares.
 */
export const labelVisibleForParcel = (
  parcel: ParcelRecord,
  zoom: number | null,
  minPx: number = LABEL_MIN_PX,
): boolean => {
  if (zoom == null) return false;
  const areaHa = parcel.shape_area;
  const lat = parcel.centroid_lat;
  if (!areaHa || areaHa <= 0 || lat == null) return false;
  const sideM = Math.sqrt(areaHa * 10000);
  return sideM / metersPerPixel(zoom, lat) >= minPx;
};

/** Keep only parcels whose centroid lies within the viewport bbox. */
export const filterParcelsToBbox = (
  parcels: ParcelRecord[],
  bbox: ParcelBbox | null | undefined,
): ParcelRecord[] => {
  if (!bbox) return parcels;
  return parcels.filter((p) => {
    const lat = p.centroid_lat;
    const lon = p.centroid_lon;
    if (lat == null || lon == null) return false;
    return (
      lon >= bbox.minLng && lon <= bbox.maxLng && lat >= bbox.minLat && lat <= bbox.maxLat
    );
  });
};

export function getParcelRef(p: ParcelRecord): string | null {
  return p.LISKEY ?? p.parcel_ref ?? null;
}

export function formatParcelLabel(p: ParcelRecord): string {
  return p.ParcelNo || p.PortionNo || '';
}

/**
 * Returns one or more polygons for a parcel. Empty array if the geometry
 * is missing or unsupported.
 */
export function parcelToPolygons(
  parcel: ParcelRecord,
  linkedRefs?: ReadonlySet<string> | null,
): ParcelPolygon[] {
  const geom = parcel.geometry;
  if (!geom || !geom.coordinates) return [];
  const ref = getParcelRef(parcel);
  const linked = Boolean(ref && linkedRefs && linkedRefs.has(ref));
  const label = formatParcelLabel(parcel) || null;

  if (geom.type === 'Polygon') {
    const ring = (geom.coordinates as [number, number][][])[0] ?? [];
    const positions = ring.map(([lng, lat]) => [lat, lng] as [number, number]);
    return [
      {
        key: ref || `geom-${Math.random()}`,
        positions,
        ref,
        label,
        linked,
      },
    ];
  }

  if (geom.type === 'MultiPolygon') {
    const polys = (geom.coordinates as [number, number][][][]) ?? [];
    return polys.map((rings, idx) => ({
      key: `${ref ?? 'geom'}-${idx}`,
      positions: (rings?.[0] ?? []).map(
        ([lng, lat]) => [lat, lng] as [number, number],
      ),
      ref,
      label,
      linked,
    }));
  }

  return [];
}

/**
 * Ray-casting point-in-polygon over a ring of Leaflet [lat, lng] pairs.
 *
 * Treats the ring as planar. Over a cadastral parcel — tens of metres — the
 * error from ignoring the earth's curvature is far below the precision of a
 * finger on a map, so the simple test is the right one here.
 */
export function pointInRing(
  point: [number, number],
  ring: Array<[number, number]>,
): boolean {
  if (ring.length < 3) return false;
  const [lat, lng] = point;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [iLat, iLng] = ring[i]!;
    const [jLat, jLng] = ring[j]!;
    const straddles = iLng > lng !== jLng > lng;
    if (!straddles) continue;
    const cross = ((jLat - iLat) * (lng - iLng)) / (jLng - iLng) + iLat;
    if (lat < cross) inside = !inside;
  }
  return inside;
}

/** First parcel whose outline contains the point, or null. */
export function parcelAtPoint(
  point: [number, number],
  parcels: ParcelRecord[],
): ParcelRecord | null {
  for (const parcel of parcels) {
    for (const poly of parcelToPolygons(parcel)) {
      if (pointInRing(point, poly.positions)) return parcel;
    }
  }
  return null;
}

const EARTH_RADIUS_M = 6371000;

/**
 * Local equirectangular projection around `origin` — treats a small patch
 * of the earth as flat, scaling longitude by cos(latitude) so the result is
 * in real metres rather than raw degrees. Same "planar is fine at parcel
 * scale" reasoning as `pointInRing`'s: this is used for a road-frontage
 * buffer of tens of metres, far below where curvature would matter.
 */
function toLocalMeters(origin: [number, number], point: [number, number]): [number, number] {
  const [lat0, lon0] = origin;
  const [lat, lon] = point;
  const y = (((lat - lat0) * Math.PI) / 180) * EARTH_RADIUS_M;
  const x = (((lon - lon0) * Math.PI) / 180) * EARTH_RADIUS_M * Math.cos((lat0 * Math.PI) / 180);
  return [x, y];
}

/** Shortest distance from point `p` to the segment `a`-`b`, in metres. */
function distanceToSegmentMeters(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): number {
  const P = toLocalMeters(a, p);
  const B = toLocalMeters(a, b);
  const lenSq = B[0] * B[0] + B[1] * B[1];
  const t = lenSq > 0 ? Math.max(0, Math.min(1, (P[0] * B[0] + P[1] * B[1]) / lenSq)) : 0;
  const dx = P[0] - t * B[0];
  const dy = P[1] - t * B[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/** Metres a parcel's road frontage can reasonably sit back from a traced
 *  centerline before it's considered "not this road" — generous enough for
 *  a rural smallholding's centroid to be well back from its boundary. */
export const ROAD_LINE_BUFFER_M = 60;

/**
 * Every parcel whose centroid lies within `bufferMeters` of the traced
 * line `points` (Leaflet [lat, lng] order) — the multi-parcel counterpart
 * to `parcelAtPoint`, used to resolve a hand-drawn road trace (e.g. tagging
 * an unnamed gravel road) into the parcels along it. Centroid-based like
 * `parcelAtPoint`'s point test, so it needs no per-parcel outline.
 */
export function parcelsNearLine(
  points: Array<[number, number]>,
  parcels: ParcelRecord[],
  bufferMeters: number = ROAD_LINE_BUFFER_M,
): ParcelRecord[] {
  if (points.length < 2) return [];
  return parcels.filter((p) => {
    const lat = p.centroid_lat;
    const lon = p.centroid_lon;
    if (lat == null || lon == null) return false;
    const centroid: [number, number] = [lat, lon];
    for (let i = 0; i < points.length - 1; i++) {
      if (distanceToSegmentMeters(centroid, points[i]!, points[i + 1]!) <= bufferMeters) {
        return true;
      }
    }
    return false;
  });
}
